import { useRef, useState } from "react";
import { Camera, Plus, Trash2, Upload, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useClerk } from "@clerk/react";
import { LegalLinks } from "@/pages/Legal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserAvatar } from "@/components/common";
import { CvButton } from "@/pages/UserProfile";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { SPECS, AVAILABILITY, GWO_PRESETS } from "@/lib/labels";
import { ProfileStrengthCard } from "@/components/ProfileStrength";

const CERT_FIELDS = [{ k: "name", label: "Name", span: "sm:col-span-5", list: "gwo-presets" }, { k: "issued", label: "Issued", type: "date", span: "sm:col-span-3" }, { k: "expires", label: "Valid until", type: "date", span: "sm:col-span-3" }];
const EXP_FIELDS = [{ k: "position", label: "Position", span: "sm:col-span-3" }, { k: "company", label: "Company", span: "sm:col-span-3" }, { k: "period", label: "Period", span: "sm:col-span-2" }, { k: "description", label: "Description", span: "sm:col-span-3" }];

const F = ({ label, children, className = "" }) => <div className={`space-y-1.5 ${className}`}><Label>{label}</Label>{children}</div>;
const Card = ({ title, children, action }) => (
  <section className="panel p-6"><div className="flex justify-between items-center mb-5"><h2 className="eyebrow">{title}</h2>{action}</div>{children}</section>
);

function useUpload(url, onDone) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { onDone(await api("post", url, fd)); toast.success("File uploaded"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return [busy, upload];
}

function ListEditor({ items, onChange, empty, fields, testPrefix }) {
  const upd = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-sm text-slate-500">{empty}</p>}
      {items.map((it, i) => (
        <div key={it._k} className="grid sm:grid-cols-12 gap-2 items-end p-3 rounded border border-slate-200 bg-slate-50" data-testid={`${testPrefix}-row-${i}`}>
          {fields.map((f) => (
            <F key={f.k} label={f.label} className={f.span}>
              <Input type={f.type || "text"} list={f.list} value={it[f.k] || ""} onChange={(e) => upd(i, f.k, e.target.value)} data-testid={`${testPrefix}-${f.k}-${i}`} className="bg-white" />
            </F>
          ))}
          <Button type="button" variant="ghost" size="icon" className="sm:col-span-1" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${testPrefix}-remove-${i}`}><Trash2 size={16} /></Button>
        </div>
      ))}
    </div>
  );
}

const csv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
const withKeys = (list = []) => list.map((x) => ({ ...x, _k: crypto.randomUUID() }));

const RADII = [["100", "Within 100 km"], ["300", "Within 300 km"], ["1000", "Within 1000 km"], ["0", "Anywhere"]];

function JobAlertsCard() {
  const api = useApi();
  const { me, setMe } = useMe();
  const radius = String(me.job_alerts_radius_km ?? 300);
  const update = async (patch, msg) => {
    try { setMe(await api("put", "/me/profile", patch)); toast.success(msg); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Job alerts">
      <label className="flex items-center gap-3">
        <Switch checked={!!me.job_alerts} data-testid="profile-job-alerts-switch"
          onCheckedChange={(v) => update({ job_alerts: v, job_alerts_radius_km: Number(radius) }, v ? "Job alerts on" : "Job alerts off")} />
        <span className="text-sm">Email me right away when a new job matching my trades is posted</span>
      </label>
      {me.job_alerts && (
        <div className="mt-4 grid sm:grid-cols-2 gap-4 items-end">
          <F label="Distance from my city">
            <Select value={radius} onValueChange={(v) => update({ job_alerts_radius_km: Number(v) }, "Alert distance saved")}>
              <SelectTrigger data-testid="profile-job-alerts-radius-select"><SelectValue /></SelectTrigger>
              <SelectContent>{RADII.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`job-alerts-radius-${v}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <p className="text-xs text-slate-500" data-testid="profile-job-alerts-hint">
            {radius !== "0" && !me.city ? "Add your city above so we can measure distance." : "Matches the trades ticked in your profile (all trades if none are ticked)."}
          </p>
        </div>
      )}
    </Card>
  );
}

function AccountCard() {
  const api = useApi();
  const { me, setMe } = useMe();
  const { signOut } = useClerk();
  const toggle = async (v) => {
    try { setMe(await api("put", "/me/profile", { email_notifications: v })); toast.success(v ? "Email notifications on" : "Email notifications off"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    const typed = window.prompt("This permanently deletes your account, profile, posts, messages and files. Type DELETE to confirm.");
    if (typed !== "DELETE") return;
    try { await api("delete", "/me"); toast.success("Your account has been deleted"); await signOut({ redirectUrl: "/" }).catch(() => { window.location.href = "/"; }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Account & privacy">
      <label className="flex items-center gap-3">
        <Switch checked={me.email_notifications !== false} onCheckedChange={toggle} data-testid="profile-email-notifications-switch" />
        <span className="text-sm">Email me about new messages, invitations and replies to my posts</span>
      </label>
      <div className="mt-6 pt-6 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
        <LegalLinks className="text-slate-500" />
        {me.access_role !== "admin" && (
          <Button type="button" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" onClick={remove} data-testid="profile-delete-account-button"><Trash2 size={15} />Delete my account</Button>
        )}
      </div>
    </Card>
  );
}

export default function ProfileEdit() {
  const api = useApi();
  const { me, setMe } = useMe();
  const isTech = me.role === "technician";
  const [f, setF] = useState({
    name: me.name || "", headline: me.headline || "", city: me.city || "", country: me.country || "", bio: me.bio || "", phone: me.phone || "",
    website: me.website || "", languages: me.languages || "", specializations: me.specializations || [], irata_level: me.irata_level || "",
    experience_years: me.experience_years ?? "", availability: me.availability || "looking",
    skills: (me.skills || []).join(", "), turbines: (me.turbines || []).join(", "),
    experience: withKeys(me.experience), certificates: withKeys(me.certificates),
  });
  const [saving, setSaving] = useState(false);
  const avatarRef = useRef(); const cvRef = useRef();
  const [avBusy, uploadAvatar] = useUpload("/me/avatar", (r) => setMe({ ...me, ...r }));
  const [cvBusy, uploadCv] = useUpload("/me/cv", (r) => setMe({ ...me, ...r }));
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const toggleSpec = (s) => setF({ ...f, specializations: f.specializations.includes(s) ? f.specializations.filter((x) => x !== s) : [...f.specializations, s] });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = isTech
      ? { ...f, skills: csv(f.skills), turbines: csv(f.turbines), experience_years: f.experience_years === "" ? null : Number(f.experience_years),
          certificates: f.certificates.filter((c) => c.name?.trim()) }
      : { name: f.name, city: f.city, country: f.country, bio: f.bio, phone: f.phone, website: f.website };
    try { setMe(await api("put", "/me/profile", body)); toast.success("Profile saved"); } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-4 max-w-5xl" data-testid="profile-edit-form">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow">My profile</p><h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">Edit profile</h1></div>
        <Button asChild variant="outline" data-testid="profile-preview-link"><Link to={`/u/${me.user_id}`}><Eye size={16} />Public preview</Link></Button>
      </div>

      <ProfileStrengthCard />

      <Card title="Basic info">
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="flex flex-col items-center gap-2">
            <UserAvatar user={me} size={96} testId="profile-edit-avatar" />
            <input ref={avatarRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => uploadAvatar(e.target.files[0])} data-testid="profile-avatar-input" />
            <Button type="button" size="sm" variant="outline" disabled={avBusy} onClick={() => avatarRef.current.click()} data-testid="profile-avatar-upload-button"><Camera size={14} />{avBusy ? "Uploading..." : "Photo"}</Button>
          </div>
          <div className="flex-1 grid sm:grid-cols-2 gap-4">
            <F label={isTech ? "Full name" : "Company name"}><Input data-testid="profile-name-input" value={f.name} onChange={set("name")} required minLength={2} /></F>
            {isTech && <F label="Headline"><Input data-testid="profile-headline-input" value={f.headline} onChange={set("headline")} placeholder="e.g. HV electrician, 7 years offshore" /></F>}
            <F label="City"><Input data-testid="profile-city-input" value={f.city} onChange={set("city")} placeholder="e.g. Sundsvall" /></F>
            <F label="Country"><Input data-testid="profile-country-input" value={f.country} onChange={set("country")} placeholder="e.g. Sweden" /></F>
            <F label="Phone"><Input data-testid="profile-phone-input" value={f.phone} onChange={set("phone")} /></F>
            {isTech ? <F label="Languages"><Input data-testid="profile-languages-input" value={f.languages} onChange={set("languages")} placeholder="English, Polish B2" /></F>
              : <F label="Website"><Input data-testid="profile-website-input" value={f.website} onChange={set("website")} /></F>}
            <F label={isTech ? "About me" : "About the company"} className="sm:col-span-2"><Textarea data-testid="profile-bio-input" rows={4} value={f.bio} onChange={set("bio")} /></F>
          </div>
        </div>
      </Card>

      {isTech && (
        <>
          <Card title="Specialization & availability">
            <div className="flex flex-wrap gap-4">
              {Object.entries(SPECS).map(([k, v]) => (
                <label key={k} className="flex items-center gap-2 text-sm"><Checkbox data-testid={`profile-spec-${k}`} checked={f.specializations.includes(k)} onCheckedChange={() => toggleSpec(k)} />{v}</label>
              ))}
            </div>
            <div className="mt-5 grid sm:grid-cols-3 gap-4">
              <F label="Years of experience"><Input type="number" min={0} max={60} data-testid="profile-years-input" value={f.experience_years} onChange={set("experience_years")} /></F>
              <F label="IRATA level">
                <Select value={f.irata_level || "none"} onValueChange={(v) => setF({ ...f, irata_level: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="profile-irata-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="1">Level 1</SelectItem><SelectItem value="2">Level 2</SelectItem><SelectItem value="3">Level 3</SelectItem></SelectContent>
                </Select>
              </F>
              <F label="Availability">
                <Select value={f.availability} onValueChange={set("availability")}>
                  <SelectTrigger data-testid="profile-availability-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(AVAILABILITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Skills (comma separated)" className="sm:col-span-3"><Input data-testid="profile-skills-input" value={f.skills} onChange={set("skills")} placeholder="HV switching, troubleshooting, hydraulics" /></F>
              <F label="Turbines / OEMs (comma separated)" className="sm:col-span-3"><Input data-testid="profile-turbines-input" value={f.turbines} onChange={set("turbines")} placeholder="Vestas V150, SG 8.0, Nordex N149" /></F>
            </div>
          </Card>

          <Card title="Certificates & qualifications" action={<Button type="button" size="sm" variant="outline" data-testid="cert-add-button" onClick={() => setF({ ...f, certificates: [...f.certificates, { name: "", issued: "", expires: "", _k: crypto.randomUUID() }] })}><Plus size={14} />Add</Button>}>
            <datalist id="gwo-presets">{GWO_PRESETS.map((g) => <option key={g} value={g} />)}</datalist>
            <ListEditor testPrefix="cert" items={f.certificates} onChange={(v) => setF({ ...f, certificates: v })} empty="Add GWO, IRATA and other trainings..."
              fields={CERT_FIELDS} />
          </Card>

          <Card title="Experience" action={<Button type="button" size="sm" variant="outline" data-testid="exp-add-button" onClick={() => setF({ ...f, experience: [...f.experience, { company: "", position: "", period: "", description: "", _k: crypto.randomUUID() }] })}><Plus size={14} />Add</Button>}>
            <ListEditor testPrefix="exp" items={f.experience} onChange={(v) => setF({ ...f, experience: v })} empty="Add your previous jobs."
              fields={EXP_FIELDS} />
          </Card>

          <Card title="CV (PDF)">
            <div className="flex flex-wrap items-center gap-3">
              <input ref={cvRef} type="file" accept="application/pdf" hidden onChange={(e) => uploadCv(e.target.files[0])} data-testid="profile-cv-input" />
              <Button type="button" variant="outline" disabled={cvBusy} onClick={() => cvRef.current.click()} data-testid="profile-cv-upload-button"><Upload size={16} />{cvBusy ? "Uploading..." : me.cv_path ? "Replace CV" : "Upload CV"}</Button>
              {me.cv_path && <><span className="text-sm text-slate-600" data-testid="profile-cv-filename">{me.cv_filename}</span><CvButton path={me.cv_path} filename={me.cv_filename} /></>}
            </div>
          </Card>
        </>
      )}

      {isTech && <JobAlertsCard />}
      <AccountCard />

      <div className="sticky bottom-20 md:bottom-4 flex justify-end">
        <Button type="submit" disabled={saving} className="h-11 px-8 shadow-lg" data-testid="profile-save-button">{saving ? "Saving..." : "Save profile"}</Button>
      </div>
    </form>
  );
}
